import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Building2, Loader2, User } from 'lucide-react'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { PlatformLogo } from '@/components/PlatformLogo'
import { useEntitySearch } from '@/hooks/use-entity-search'
import {
  allNavGroups,
  homeItem,
  playbookItems,
  metricsItems,
  platformItems,
  type NavItem,
} from '@/lib/navigation'

// Quick action items - frequently used pages
const quickActionItems: NavItem[] = [
  homeItem,
  playbookItems.items.find(i => i.title === 'Changelog')!,
  metricsItems.items.find(i => i.title === 'Funnel')!,
  platformItems.items.find(i => i.title === 'Users')!,
  platformItems.items.find(i => i.title === 'Command Log')!,
]

interface PageEntry {
  item: NavItem
  /** Sidebar section, shown as a right-side hint in search results ('' for Home). */
  section: string
}

const searchablePages: PageEntry[] = [
  { item: homeItem, section: '' },
  ...allNavGroups.flatMap(group => group.items.map(item => ({ item, section: group.title }))),
]

const MAX_PAGE_RESULTS = 8

/**
 * Deterministic page ranking. cmdk's built-in fuzzy filter subsequence-matches
 * across the whole title+keywords blob ("busin" matches scattered letters of
 * "Monetization ... subscriptions payments"), and its group re-sort is a silent
 * no-op, so a garbage match in an earlier group outranked an exact title
 * prefix. We filter with shouldFilter={false} and score ourselves: title match
 * always beats keyword match, prefix beats substring.
 */
function scorePage(entry: PageEntry, q: string): number {
  const title = entry.item.title.toLowerCase()
  if (title === q) return 7
  if (title.startsWith(q)) return 6
  if (title.split(/\s+/).some(word => word.startsWith(q))) return 5
  if (title.includes(q)) return 4
  const keywords = (entry.item.keywords ?? []).map(k => k.toLowerCase())
  if (keywords.some(k => k.startsWith(q))) return 3
  if (keywords.some(k => k.includes(q))) return 2
  const section = entry.section.toLowerCase()
  if (section.startsWith(q)) return 1
  const tokens = q.split(/\s+/).filter(Boolean)
  if (tokens.length > 1) {
    const haystacks = [title, ...keywords, section]
    if (tokens.every(token => haystacks.some(h => h.includes(token)))) return 1
  }
  return 0
}

function rankPages(query: string): PageEntry[] {
  const q = query.trim().toLowerCase()
  if (!q) return []
  return searchablePages
    .map((entry, index) => ({ entry, index, score: scorePage(entry, q) }))
    .filter(scored => scored.score > 0)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .slice(0, MAX_PAGE_RESULTS)
    .map(scored => scored.entry)
}

// Role badge colors, mirroring the Users page convention (plain 'user' is hidden)
function getRoleBadgeClass(role: string): string {
  switch (role) {
    case 'admin':
      return 'bg-red-500/10 text-red-500 border-red-500/30'
    case 'moderator':
      return 'bg-accent/10 text-accent border-accent/30'
    case 'developer':
      return 'bg-primary/10 text-primary border-primary/30'
    case 'system':
      return 'bg-amber-500/10 text-amber-500 border-amber-500/30'
    default:
      return 'hidden'
  }
}

// Compact domain from a business website URL ("https://www.acme.com/x" → "acme.com")
function formatDomain(website?: string): string | null {
  if (!website) return null
  try {
    const url = new URL(website.includes('://') ? website : `https://${website}`)
    return url.hostname.replace(/^www\./, '')
  } catch {
    return website
  }
}

// Custom event name for opening the command palette
export const OPEN_COMMAND_PALETTE_EVENT = 'open-command-palette'

// Helper function to open the command palette from anywhere
// eslint-disable-next-line react-refresh/only-export-components -- imperative opener used by the sidebar shortcut
export function openCommandPalette() {
  window.dispatchEvent(new CustomEvent(OPEN_COMMAND_PALETTE_EVENT))
}

export function CommandPalette() {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const navigate = useNavigate()

  // Entity search (users / businesses / campaigns) kicks in at 2+ chars,
  // debounced inside the hook. `entity.query` is the debounced value.
  const entity = useEntitySearch(open ? search : '')

  const query = search.trim()
  const searching = query.length > 0
  const rankedPages = useMemo(() => rankPages(search), [search])
  // Bridge the debounce gap so "No results found." doesn't flash before the
  // entity queries even start.
  const entityPending = query.length >= 2 && !entity.active

  // Selection is controlled: cmdk re-picks a selection whenever async entity
  // rows replace each other, which can land on a random mid-list row. We pin
  // the selection to the top result as results stream in, unless the user has
  // arrow-navigated since the last keystroke.
  const [selectedValue, setSelectedValue] = useState('')
  const userNavigatedRef = useRef(false)

  const firstValue = useMemo(() => {
    if (!searching) return `quick:${homeItem.href}`
    if (rankedPages.length > 0) return `page:${rankedPages[0].item.href}`
    if (entity.users.data.length > 0) return `user:${entity.users.data[0].id}`
    if (entity.businesses.data.length > 0) return `business:${entity.businesses.data[0].id}`
    if (entity.campaigns.data.length > 0) return `campaign:${entity.campaigns.data[0].id}`
    return ''
  }, [searching, rankedPages, entity.users.data, entity.businesses.data, entity.campaigns.data])

  useEffect(() => {
    userNavigatedRef.current = false
  }, [search])

  useEffect(() => {
    if (!userNavigatedRef.current) {
      setSelectedValue(firstValue)
    }
  }, [firstValue])

  const handleInputKeyDown = useCallback((e: React.KeyboardEvent) => {
    const navigationKeys = ['ArrowDown', 'ArrowUp', 'Home', 'End']
    if (navigationKeys.includes(e.key) || (e.ctrlKey && ['n', 'p', 'j', 'k'].includes(e.key))) {
      userNavigatedRef.current = true
    }
  }, [])

  // Global keyboard shortcut: Cmd+K (Mac) or Ctrl+K (Windows/Linux)
  // Also listen for custom event to open palette
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen(open => !open)
      }
    }

    const handleOpenEvent = () => {
      setOpen(true)
    }

    document.addEventListener('keydown', down)
    window.addEventListener(OPEN_COMMAND_PALETTE_EVENT, handleOpenEvent)

    return () => {
      document.removeEventListener('keydown', down)
      window.removeEventListener(OPEN_COMMAND_PALETTE_EVENT, handleOpenEvent)
    }
  }, [])

  const runCommand = useCallback((command: () => void) => {
    setOpen(false)
    setSearch('')
    setSelectedValue('')
    userNavigatedRef.current = false
    command()
  }, [])

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      setSearch('')
      setSelectedValue('')
      userNavigatedRef.current = false
    }
  }, [])

  // Filtering, ranking, and ordering are all done here (shouldFilter=false):
  // cmdk just renders our list and handles keyboard selection. Every item gets
  // a stable, unique value (hrefs / entity ids) so selection never lands on
  // two rows at once and never resets when async results stream in.
  return (
    <CommandDialog
      open={open}
      onOpenChange={handleOpenChange}
      loop
      shouldFilter={false}
      value={selectedValue}
      onValueChange={setSelectedValue}
    >
      <CommandInput
        placeholder="Search pages, users, businesses, campaigns..."
        value={search}
        onValueChange={setSearch}
        onKeyDown={handleInputKeyDown}
      />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {/* Browse mode: no query yet, show the full nav. */}
        {!searching && (
          <>
            <CommandGroup heading="Quick Actions">
              {quickActionItems.map(item => (
                <CommandItem
                  key={`quick-${item.href}`}
                  value={`quick:${item.href}`}
                  onSelect={() => runCommand(() => navigate(item.href))}
                >
                  <item.icon className="mr-2 h-4 w-4" />
                  <span>{item.title}</span>
                </CommandItem>
              ))}
            </CommandGroup>

            {allNavGroups.map(group => (
              <Fragment key={group.title}>
                <CommandSeparator />
                <CommandGroup heading={group.title}>
                  {group.items.map(item => (
                    <CommandItem
                      key={item.href}
                      value={`${group.title}:${item.href}`}
                      onSelect={() => runCommand(() => navigate(item.href))}
                    >
                      <item.icon className="mr-2 h-4 w-4" />
                      <span>{item.title}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </Fragment>
            ))}
          </>
        )}

        {/* Search mode: one flat, ranked group. Best match is first = selected. */}
        {searching && rankedPages.length > 0 && (
          <CommandGroup heading="Pages">
            {rankedPages.map(({ item, section }) => (
              <CommandItem
                key={`page-${item.href}`}
                value={`page:${item.href}`}
                onSelect={() => runCommand(() => navigate(item.href))}
              >
                <item.icon className="mr-2 h-4 w-4" />
                <span>{item.title}</span>
                {section && (
                  <span className="ml-auto shrink-0 text-xs text-muted-foreground">{section}</span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {entityPending && rankedPages.length === 0 && (
          <CommandItem value="palette:pending" disabled>
            <Loader2 className="mr-2 h-4 w-4 animate-spin text-muted-foreground" />
            <span className="text-muted-foreground">Searching...</span>
          </CommandItem>
        )}

        {/* Entity search results (2+ chars, debounced). */}
        {entity.active && (
          <>
            {(entity.users.loading || entity.users.data.length > 0) && (
              <>
                <CommandSeparator />
                <CommandGroup heading="Users">
                  {entity.users.loading ? (
                    <CommandItem value="palette:users:loading" disabled>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin text-muted-foreground" />
                      <span className="text-muted-foreground">Searching users...</span>
                    </CommandItem>
                  ) : (
                    entity.users.data.map(user => (
                      <CommandItem
                        key={`user-${user.id}`}
                        value={`user:${user.id}`}
                        onSelect={() => runCommand(() => navigate(`/platform/users/${user.id}`))}
                      >
                        <User className="mr-2 h-4 w-4" />
                        <div className="flex min-w-0 flex-1 items-baseline gap-2">
                          <span className="truncate">{user.name || user.email}</span>
                          {user.name && (
                            <span className="truncate text-xs text-muted-foreground">
                              {user.email}
                            </span>
                          )}
                        </div>
                        {user.role !== 'user' && (
                          <span
                            className={`ml-auto shrink-0 rounded border px-1.5 py-0.5 text-[10px] font-medium ${getRoleBadgeClass(user.role)}`}
                          >
                            {user.role}
                          </span>
                        )}
                      </CommandItem>
                    ))
                  )}
                </CommandGroup>
              </>
            )}

            {(entity.businesses.loading || entity.businesses.data.length > 0) && (
              <>
                <CommandSeparator />
                <CommandGroup heading="Businesses">
                  {entity.businesses.loading ? (
                    <CommandItem value="palette:businesses:loading" disabled>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin text-muted-foreground" />
                      <span className="text-muted-foreground">Searching businesses...</span>
                    </CommandItem>
                  ) : (
                    entity.businesses.data.map(business => {
                      const domain = formatDomain(business.website)
                      return (
                        <CommandItem
                          key={`business-${business.id}`}
                          value={`business:${business.id}`}
                          onSelect={() =>
                            runCommand(() => navigate(`/platform/businesses/${business.id}`))
                          }
                        >
                          <Building2 className="mr-2 h-4 w-4" />
                          <div className="flex min-w-0 flex-1 items-baseline gap-2">
                            <span className="truncate">{business.name}</span>
                            <span className="truncate text-xs text-muted-foreground">
                              {domain || `${business.memberCount} members`}
                            </span>
                          </div>
                        </CommandItem>
                      )
                    })
                  )}
                </CommandGroup>
              </>
            )}

            {(entity.campaigns.loading || entity.campaigns.data.length > 0) && (
              <>
                <CommandSeparator />
                <CommandGroup heading="Campaigns">
                  {entity.campaigns.loading ? (
                    <CommandItem value="palette:campaigns:loading" disabled>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin text-muted-foreground" />
                      <span className="text-muted-foreground">Searching campaigns...</span>
                    </CommandItem>
                  ) : (
                    entity.campaigns.data.map(campaign => (
                      <CommandItem
                        key={`campaign-${campaign.id}`}
                        value={`campaign:${campaign.id}`}
                        onSelect={() => runCommand(() => navigate('/metrics/campaigns'))}
                      >
                        <PlatformLogo platform={campaign.platform} size="sm" className="mr-2" />
                        <span className="truncate">{campaign.campaignName}</span>
                        <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                          {campaign.platformDisplay}
                        </span>
                      </CommandItem>
                    ))
                  )}
                </CommandGroup>
              </>
            )}
          </>
        )}
      </CommandList>
    </CommandDialog>
  )
}
